import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import InputRoundedIcon from "@mui/icons-material/InputRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import {
  groupAutomationEvents,
  findEnrichedEvent,
} from "../utils/automationEventCatalog";

const GROUP_ICONS = {
  entrada: InputRoundedIcon,
  alteracao: EditNoteRoundedIcon,
  tempo: ScheduleRoundedIcon,
  chatbot: SmartToyOutlinedIcon,
  outros: MoreHorizRoundedIcon,
};

const TIMING_CHIP = {
  pontual: { color: "info", icon: EventRoundedIcon },
  recorrente: { color: "warning", icon: ScheduleRoundedIcon },
  preventivo: { color: "error", icon: ShieldRoundedIcon },
};

function TimingBadge({ timing, label, compact }) {
  const cfg = TIMING_CHIP[timing] || TIMING_CHIP.pontual;
  const Icon = cfg.icon;
  return (
    <Chip
      size="small"
      color={cfg.color}
      variant={compact ? "outlined" : "filled"}
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={label}
      sx={{ fontWeight: 500 }}
    />
  );
}

function EventCard({ evento, selected, onSelect }) {
  const theme = useTheme();
  const { meta } = evento;

  return (
    <Paper
      component="button"
      type="button"
      onClick={() => onSelect(evento.id)}
      elevation={0}
      sx={{
        p: 1.75,
        textAlign: "left",
        width: "100%",
        cursor: "pointer",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        borderRadius: 2,
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.06)
          : "background.paper",
        transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          borderColor: selected ? "primary.main" : "primary.light",
          bgcolor: selected
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.primary.main, 0.03),
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {meta.displayTitle}
            </Typography>
            {meta.displayTitle !== evento.nome ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {evento.nome}
              </Typography>
            ) : null}
          </Box>
          {selected ? (
            <CheckCircleRoundedIcon color="primary" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
          ) : null}
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 6,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {meta.description}
        </Typography>

        <TimingBadge timing={meta.timing} label={meta.timingLabel} compact />
      </Stack>
    </Paper>
  );
}

export default function AutomationEventPicker({ eventos = [], value, onChange, error }) {
  const [search, setSearch] = useState("");
  const selectedId = Number(value) || "";

  const groups = useMemo(() => groupAutomationEvents(eventos, search), [eventos, search]);
  const selected = useMemo(
    () => findEnrichedEvent(eventos, selectedId),
    [eventos, selectedId]
  );

  const totalVisible = groups.reduce((acc, g) => acc + g.events.length, 0);

  return (
    <Stack spacing={2}>
      <Alert severity="info" icon={false} sx={{ py: 1.25 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quando esta regra deve disparar?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          O evento é o momento do disparo. Num evento pontual, só a conversa daquele momento entra.
          Numa regra de tempo, conversas antigas ficam de fora.
        </Typography>
      </Alert>

      <TextField
        size="small"
        fullWidth
        placeholder="Buscar evento por nome ou descrição…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {totalVisible === 0 ? (
        <Alert severity="warning">
          Nenhum evento encontrado{search ? ` para “${search}”` : ""}. Tente outro termo ou
          verifique se o objeto foi selecionado corretamente.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {groups.map((group) => {
            const GroupIcon = GROUP_ICONS[group.id] || MoreHorizRoundedIcon;
            return (
              <Box key={group.id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <GroupIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {group.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.description}
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                    gap: 1.5,
                  }}
                >
                  {group.events.map((evt) => (
                    <EventCard
                      key={evt.id}
                      evento={evt}
                      selected={Number(evt.id) === Number(selectedId)}
                      onSelect={onChange}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}

      {error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : null}

      {selected ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderLeft: "4px solid",
            borderLeftColor: "primary.main",
            bgcolor: "background.default",
          }}
        >
          <Stack spacing={1.25}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {selected.meta.displayTitle}
              </Typography>
              <TimingBadge timing={selected.meta.timing} label={selected.meta.timingLabel} />
              <Chip size="small" variant="outlined" label={selected.meta.groupLabel} />
            </Box>

            <Typography variant="body2">{selected.meta.description}</Typography>

            <Typography variant="caption" color="text.secondary">
              {selected.meta.timingHint}
            </Typography>

            {selected.meta.example ? (
              <Alert severity="success" icon={false} sx={{ py: 0.75 }}>
                <Typography variant="caption" color="text.secondary">
                  Exemplo de uso
                </Typography>
                <Typography variant="body2">{selected.meta.example}</Typography>
              </Alert>
            ) : null}

            {selected.nome === "Acionado pelo ChatBot" ||
            selected.meta.displayTitle === "Acionado pelo ChatBot" ? (
              <Alert severity="info" sx={{ py: 0.75 }}>
                Vincule esta regra em um passo do ChatBot (ação{" "}
                <strong>Regras de automação</strong>). Ela não dispara automaticamente por
                alteração de solicitação.
              </Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : (
        <Alert severity="info" variant="outlined">
          Selecione um evento acima para ver detalhes e continuar.
        </Alert>
      )}
    </Stack>
  );
}
